import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { C } from '@ridgewill/brand';

/**
 * A dropdown drawn by the portal rather than by the browser (account#91).
 *
 * Copied from RidgeWill/account's `src/components/Select.tsx`, where it is
 * tested with a DOM harness this portal does not have (Jon, 2026-09-27: "look
 * like the ones from the Account portal"). Keep the two in step.
 *
 * ── WHY NOT A NATIVE `<select>` ──────────────────────────────────────
 *
 * The browser draws a native select's open list itself, and on Windows Chrome
 * that list ignores the page's theme in ways CSS cannot reach: with a
 * transparent control it comes out white with the dark theme's light text on
 * it, even with `color-scheme: dark` set. Jon saw exactly that on the "Who can
 * invite you" control and on the theme picker, and asked for a list that looks
 * like the portal rather than like the operating system.
 *
 * ── WHAT IT OWES A KEYBOARD AND A SCREEN READER ──────────────────────
 *
 * The native control gave those for free, so they are written out here, after
 * the WAI-ARIA "select-only combobox" pattern:
 *
 *   - the trigger is `role="combobox"` with `aria-expanded`, and the list is a
 *     `role="listbox"` of `role="option"`s with `aria-selected`;
 *   - focus stays on the trigger, and the highlighted option is named by
 *     `aria-activedescendant`, so a screen reader follows the highlight;
 *   - ArrowUp/ArrowDown, Enter and Space open it; ArrowUp/ArrowDown, Home and
 *     End move the highlight; Enter or Space chooses; Escape and Tab close
 *     without choosing; a click outside closes it too.
 *
 * ── WHY THE OPEN LIST IS PORTALLED TO <body> ─────────────────────────
 *
 * It used to hang off the trigger with `position: absolute`. Every Panel in
 * this portal is `overflow: hidden` (for its rounded corners), so a list that
 * opened near the bottom of one was cut off at the Panel's edge: Jon saw the
 * Status list on a Support ticket vanish into the Conversation panel below it.
 * No z-index escapes an ancestor that clips. The list is therefore rendered
 * into <body> with `position: fixed`, placed from the trigger's own rectangle,
 * re-placed on any scroll or resize, and opened upwards when there is more
 * room above than below. Its z-index sits above the Modal (40), because half
 * of these controls live inside one.
 *
 * Portalling changes two things the code below relies on. The outside-click
 * test checks the list as well as the trigger, since the list is no longer
 * inside `wrapRef`. And Escape stops propagating while the list is open, so it
 * closes the list without also closing the Modal around it (Modal listens on
 * `document`).
 */
export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  labelledBy,
  disabled = false,
  size = 'md',
  align = 'start',
  block = false,
  placeholder,
}: {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  /** The accessible name when nothing visible labels the control. */
  label?: string;
  /** The id of a visible label, when there is one. */
  labelledBy?: string;
  disabled?: boolean;
  /** `sm` for the header, `md` inside a page. */
  size?: 'sm' | 'md';
  /** Which edge of the control the open list lines up with. `end` near the right of the page. */
  align?: 'start' | 'end';
  /** Fill the width of its container, like the form inputs beside it. */
  block?: boolean;
  /** Shown, muted, while `value` matches no option (the login app's Month field). */
  placeholder?: string;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const [open, setOpen] = useState(false);
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const [active, setActive] = useState(selectedIndex);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [place, setPlace] = useState<React.CSSProperties | null>(null);

  // Opening always starts on the current choice, not wherever the highlight
  // was left last time.
  function openList() {
    if (disabled) return;
    setActive(selectedIndex);
    setOpen(true);
  }

  function choose(i: number) {
    const option = options[i];
    setOpen(false);
    if (option && option.value !== value) onChange(option.value);
  }

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !listRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Placed before paint, so the list never flashes at the wrong spot. Scroll
  // is listened for in the capture phase, which catches a Modal's own scroll
  // container as well as the page.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const measure = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      if (!t) return;
      const gap = 4;
      const margin = 8;
      const below = window.innerHeight - t.bottom - gap - margin;
      const above = t.top - gap - margin;
      const wanted = listRef.current?.scrollHeight ?? 0;
      const up = wanted > below && above > below;
      const horizontal =
        align === 'end' ? { right: window.innerWidth - t.right } : { left: t.left };
      setPlace({
        ...horizontal,
        minWidth: t.width,
        maxHeight: Math.max(120, up ? above : below),
        ...(up ? { bottom: window.innerHeight - t.top + gap } : { top: t.bottom + gap }),
      });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, align]);

  // A long list scrolls, so the highlight has to follow the keyboard. Called
  // optionally because jsdom, which the tests run in, has no scrollIntoView.
  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    const last = options.length - 1;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => Math.min(last, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(last);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(active);
        break;
      case 'Escape':
        e.preventDefault();
        // One Escape, one layer: the list, not the Modal it sits in.
        e.stopPropagation();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  }

  const pad = size === 'sm' ? '.55rem' : '.65rem';
  const matched = options.some((o) => o.value === value);
  const current = matched || !placeholder ? options[selectedIndex] : null;

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: block ? 'block' : 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? optionId(active) : undefined}
        aria-label={label}
        aria-labelledby={labelledBy}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        style={{
          ...trigger,
          ...(block ? { display: 'flex', width: '100%' } : {}),
          padding: size === 'sm' ? `.3rem ${pad}` : `.4rem ${pad}`,
          fontSize: size === 'sm' ? '.85rem' : '.9rem',
          color: size === 'sm' ? C.textMid : C.text,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', ...(current ? {} : { color: C.muted }) }}>
          {current ? current.label : placeholder}
        </span>
        <Chevron up={open} />
      </button>
      {open && createPortal(
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-labelledby={labelledBy}
          aria-label={label}
          // Hidden until measured, so the first frame is not drawn at 0,0.
          style={{ ...list, ...(place ?? { top: 0, left: 0, visibility: 'hidden' }) }}
        >
          {options.map((option, i) => {
            const selected = i === selectedIndex;
            return (
              <li
                key={option.value}
                id={optionId(i)}
                role="option"
                aria-selected={selected}
                // mousedown, not click: it fires before the trigger loses
                // anything, and preventDefault keeps focus on the trigger.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(i);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...item,
                  padding: `.4rem ${pad}`,
                  fontSize: size === 'sm' ? '.85rem' : '.9rem',
                  background: i === active ? C.border2 : 'transparent',
                  fontWeight: selected ? 600 : 400,
                }}
              >
                <span>{option.label}</span>
                <span aria-hidden="true" style={{ width: 14, color: C.brand, visibility: selected ? 'visible' : 'hidden' }}>
                  <Check />
                </span>
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </div>
  );
}

function Chevron({ up }: { up: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      style={{ flexShrink: 0, color: C.textMid2, transform: up ? 'rotate(180deg)' : undefined, transition: 'transform 120ms' }}
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" style={{ display: 'block' }}>
      <path d="M3 7.5 5.75 10 11 4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const trigger: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '.6rem',
  background: C.surface,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  fontFamily: 'inherit',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const list: React.CSSProperties = {
  position: 'fixed',
  zIndex: 60,
  overflowY: 'auto',
  boxSizing: 'border-box',
  margin: 0,
  padding: 4,
  listStyle: 'none',
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(10, 15, 30, 0.18)',
};

const item: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  borderRadius: 5,
  color: C.text,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
};
